import type { Translations } from '@/lib/i18n';
import { FUENTES_EDITOR } from '@/lib/orders/fuentes-editor';
import { ASPECTOS_ILEGIBLES, normalizarAspectos, type Aspecto, type AspectosFormato, type AspectosGuardados } from './aspectos';

/**
 * ¿Se ofrece la función de plantillas de formato?
 *
 * Opt-in a propósito, como `NEXT_PUBLIC_CORPUS_ENABLED`: si alguien monta un
 * entorno y se olvida de la variable, la función queda apagada, que es el lado
 * seguro. En producción NO se define — el código viaja pero la función no se
 * ofrece todavía. Al encenderla hay que REDESPLEGAR: las NEXT_PUBLIC_* se
 * hornean en el build, guardarla en Vercel no basta.
 */
export const FORMATOS_ENABLED = process.env.NEXT_PUBLIC_FORMAT_TEMPLATES_ENABLED === 'true';

/**
 * Plantillas de formato (Opción 4). Espejo del `FormatTemplateDto` de
 * processing-ms: los decimales ya llegan como number. Los catálogos de valores
 * son los mismos que valida el backend (`validar`).
 */
export interface FormatTemplate {
  id: string;
  accountId: string | null;
  sourceTemplateId: string | null;
  name: string;
  description: string | null;
  isGlobal: boolean;
  isDefault: boolean;
  isActive: boolean;
  locale: string | null;
  pageFormat: 'a4' | 'letter' | 'legal';
  pageOrientation: 'portrait' | 'landscape';
  marginTopMm: number;
  marginBottomMm: number;
  marginLeftMm: number;
  marginRightMm: number;
  fontFamily: string;
  fontSizePt: number;
  lineHeight: number;
  textAlign: 'justify' | 'left' | 'center' | 'right';
  paragraphSpaceAfterPt: number;
  headerEnabled: boolean;
  headerText: string | null;
  headerAlign: 'left' | 'center' | 'right';
  headerMode: 'all' | 'first' | 'rest';
  headerFontSizePt: number | null;
  footerEnabled: boolean;
  footerText: string | null;
  pageNumbering: boolean;
  pageNumberFormat: 'n' | 'n_de_m' | '00n';
  pageNumberStart: number;
  pageNumberPosition: 'footer-center' | 'footer-right' | 'header-right';
  fillShortLines: boolean;
  fillChar: string;
  fillScope: 'paragraph-end' | 'line' | 'blank-only';
  closingFormulaEnabled: boolean;
  closingFormulaHtml: string | null;
  closingKeepTogether: boolean;
  closingLanguage: 'es' | 'target';
  nameI18n?: Record<string, string> | null;
  // v4 — identidad de biblioteca (solo lectura: la escribe el sistema)
  slug?: string | null;
  category?: 'general' | 'certified' | 'court';
  origin?: 'verified' | 'reference' | 'own';
  country?: string | null;
  standardRef?: string | null;
  lockedFields?: string[];
  lockMessage?: string | null;
  isSystem?: boolean;
  // v4 — configuración
  unit?: 'cm' | 'in';
  thumbnail?: string | null;
  sampleDoc?: string;
  isPinned?: boolean;
  // Formato mixto (032): null = formato completo. `aspectosBloqueados` y `esMixta` los calcula el servidor.
  // El servidor la sirve compacta (sin `orientacion` si es la de la hoja); el
  // formulario la lleva entera (templateToInput) y la compacta al guardar.
  aspectos?: AspectosFormato | AspectosGuardados | null;
  aspectosBloqueados?: Aspecto[];
  esMixta?: boolean;
  mirrorsSource?: boolean;
  headerColumns?: BandColumn[];
  footerColumns?: BandColumn[];
  footerRule?: boolean;
  /**
   * Encuadernación en mm: el backend la suma al izquierdo. El editor ya no la
   * muestra (pedido de Andrey, 3 oct 2026): al cargar, `plegarEncuadernacion`
   * la pasa al izquierdo y la deja en 0.
   */
  bindingMarginMm?: number;
  /**
   * Distancia del encabezado y del pie al borde, en mm (w:header / w:footer
   * de Word; 12,5 por defecto). Opcionales a propósito: si el backend no las
   * manda, el editor usa 12,5 sin añadirlas a lo que se guarda.
   */
  headerDistanceMm?: number;
  footerDistanceMm?: number;
  singleSided?: boolean;
  fillSkipTables?: boolean;
  lineNumbering?: boolean;
  linesPerPage?: number | null;
  lineNumberRule?: boolean;
  describeIllegible?: boolean;
  repeatSealSignature?: boolean;
  invalidateBack?: boolean;
  sequentialNumbering?: boolean;
  sequentialPrefix?: string | null;
  sequentialStart?: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  accountName?: string | null;
}

/** Una celda del encabezado o del pie (hasta cuatro por banda). */
export interface BandColumn {
  type: 'empty' | 'text' | 'image' | 'pagenum';
  /** Texto plano, siempre: el respaldo de quien no conozca `html` (validación, resúmenes, plantillas viejas). */
  text?: string | null;
  /**
   * El mismo texto con negrita, cursiva o subrayado: solo <strong>, <em>,
   * <u> y <br>, sin atributos (`normalizarTextoBloque`). Sin formato no se
   * guarda: la columna queda como siempre y processing-ms pinta `text`.
   */
  html?: string | null;
  image?: 'logo' | 'signature' | 'seal' | null;
  /** Imagen PROPIA subida a S3 (URL pública del gateway); manda sobre la del perfil. */
  imageUrl?: string | null;
  size?: 's' | 'm' | 'l' | null;
  /** Alto personalizado en mm (5-60); manda sobre `size`. */
  heightMm?: number | null;
  align?: 'left' | 'center' | 'right';
  /**
   * Encabezado o pie en un solo campo: la banda entera como HTML canónico
   * (`normalizarBandaRica`). Va en UNA columna de tipo 'text' para que un
   * processing-ms viejo la entienda y pinte su `text`, que es el respaldo.
   */
  richHtml?: string | null;
}

/**
 * Lo que viaja al crear/editar: todo menos identidad, alcance y fechas. La
 * identidad de biblioteca (slug/origin/candados) tampoco viaja: el backend la
 * ignora y la fija él.
 */
export type FormatTemplateInput = Omit<
  FormatTemplate,
  | 'id' | 'accountId' | 'sourceTemplateId' | 'isGlobal' | 'isDefault' | 'createdBy' | 'createdAt' | 'updatedAt' | 'accountName'
  | 'slug' | 'origin' | 'standardRef' | 'lockedFields' | 'lockMessage' | 'isSystem'
  | 'aspectosBloqueados' | 'esMixta'
>;

export const PAGE_FORMATS = ['a4', 'letter', 'legal'] as const;
export const ORIENTATIONS = ['portrait', 'landscape'] as const;
export const TEXT_ALIGNS = ['justify', 'left', 'center', 'right'] as const;
export const ALIGNS = ['left', 'center', 'right'] as const;
export const HEADER_MODES = ['all', 'first', 'rest'] as const;
export const PAGE_NUMBER_FORMATS = ['n', 'n_de_m', '00n'] as const;
export const PAGE_NUMBER_POSITIONS = ['footer-center', 'footer-right', 'header-right'] as const;
export const FILL_SCOPES = ['paragraph-end', 'line', 'blank-only'] as const;

/**
 * Letras de la plantilla: las mismas del editor de Revisión (las de Office y
 * las libres de Google Fonts), que salen iguales en el editor, el PDF, el Word y
 * Google Docs. El selector las agrupa con GRUPOS_FUENTES. Una plantilla guardada
 * con otra (Garamond, Verdana, Liberation…) la conserva; solo deja de poder elegirse.
 */
export const FONT_FAMILIES: readonly string[] = FUENTES_EDITOR.map((f) => f.titulo);

/** Variables SUGERIDAS en el editor (chips). El backend sigue sustituyendo
 *  también las retiradas de esta lista ({titulo}, {numero_caso}) si alguna
 *  plantilla vieja las trae — solo dejaron de ofrecerse (pedido de Andrey,
 *  7 sep 2026: fuera lo del documento/título; páginas se queda). */
// Depuradas a pedido de Andrey (7 sep 2026): fuera matricula, correlativo y
// las variantes _en — {idioma_origen}/{idioma_destino}/{mes} ya hablan solos
// el idioma DESTINO de la traducción (i18n en el backend). El backend sigue
// sustituyendo también las retiradas si una fórmula vieja las usa.
export const TEMPLATE_VARIABLES = [
  'idioma_origen', 'idioma_destino', 'ciudad', 'dia', 'mes', 'año',
  'traductor', 'total_paginas',
] as const;

/** Los mismos tokens en inglés (el backend acepta ambos como alias). */
export const TEMPLATE_VARIABLES_EN = [
  'source_language', 'target_language', 'city', 'day', 'month', 'year',
  'translator', 'total_pages',
] as const;

/** Chips según el idioma de la interfaz (pedido de Andrey, 7 sep 2026):
 *  español → tokens en español (con {año} de verdad); el resto → inglés. */
export const templateVariablesFor = (locale: string): readonly string[] =>
  locale === 'es' ? TEMPLATE_VARIABLES : TEMPLATE_VARIABLES_EN;

const CLOSING_FORMULA_DEFAULT =
  '<p>Es traducción fiel al {idioma_destino} del documento original redactado en {idioma_origen} que he tenido ante mí y al cual me remito. En {ciudad}, a los {dia} días del mes de {mes} de {anio}.</p>' +
  '<p>This is a true and faithful translation into {idioma_destino_en} of the original document written in {idioma_origen_en}, which I have had before me and to which I refer. In {ciudad}, on the {dia} day of {mes_en}, {anio}.</p>' +
  '<p>{traductor} — Mat. {matricula}.</p>';

/** Valores de una plantilla nueva: la norma CTPCBA con Arial 11 (lo que el usuario pidió como base). */
export const EMPTY_TEMPLATE_INPUT: FormatTemplateInput = {
  name: '',
  description: '',
  isActive: true,
  locale: null,
  pageFormat: 'a4',
  pageOrientation: 'portrait',
  marginTopMm: 25,
  marginBottomMm: 25,
  marginLeftMm: 30,
  marginRightMm: 20,
  fontFamily: 'Arial',
  fontSizePt: 11,
  lineHeight: 1.5,
  textAlign: 'justify',
  paragraphSpaceAfterPt: 0,
  headerEnabled: true,
  headerText: 'TRADUCCIÓN PÚBLICA',
  headerAlign: 'center',
  headerMode: 'all',
  headerFontSizePt: null,
  footerEnabled: false,
  footerText: '',
  pageNumbering: true,
  pageNumberFormat: 'n_de_m',
  pageNumberStart: 1,
  pageNumberPosition: 'footer-center',
  fillShortLines: true,
  fillChar: '-',
  fillScope: 'paragraph-end',
  closingFormulaEnabled: true,
  closingFormulaHtml: CLOSING_FORMULA_DEFAULT,
  closingKeepTogether: true,
  closingLanguage: 'es',
};

/**
 * La encuadernación pasa al margen izquierdo y queda en 0: el backend la suma
 * al izquierdo al aplicar, así que el documento sale igual y el editor no
 * necesita el campo. Sin encuadernación (todas las plantillas de hoy)
 * devuelve el mismo objeto: lo que se guarda no cambia ni un byte.
 *
 * Si la norma bloquea el izquierdo o la encuadernación no se toca: el backend
 * rechazaría con un 409 cualquier guardado que los cambie.
 */
export function plegarEncuadernacion<T extends { marginLeftMm: number; bindingMarginMm?: number }>(v: T, bloqueados: readonly string[] = []): T {
  const enc = Number(v.bindingMarginMm);
  if (!Number.isFinite(enc) || enc <= 0) return v;
  if (bloqueados.includes('marginLeftMm') || bloqueados.includes('bindingMarginMm')) return v;
  // A la décima de mm, como guarda validar(): 30 + 10,1 da 40,1 y no 40,10000000000001.
  return { ...v, marginLeftMm: Math.round(((Number(v.marginLeftMm) || 0) + enc) * 10) / 10, bindingMarginMm: 0 };
}

export function templateToInput(t: FormatTemplate): FormatTemplateInput {
  const {
    id: _id, accountId: _a, sourceTemplateId: _s, isGlobal: _g, isDefault: _d, createdBy: _c,
    createdAt: _ca, updatedAt: _ua, accountName: _an,
    slug: _sl, origin: _o, standardRef: _sr, lockedFields: _lf, lockMessage: _lm, isSystem: _is,
    aspectosBloqueados: _ab, esMixta: _em,
    ...input
  } = t;
  // La mezcla, entera (con la orientación heredada de la hoja): así el
  // formulario compara y cambia siempre la misma forma. Ilegible, tal cual.
  const n = input.aspectos == null ? null : normalizarAspectos(input.aspectos);
  const aspectos = n?.ok ? n.valor : input.aspectos;
  return plegarEncuadernacion({ ...EMPTY_TEMPLATE_INPUT, ...input, ...(input.aspectos === undefined ? {} : { aspectos }), description: input.description ?? '', footerText: input.footerText ?? '' }, t.lockedFields ?? []);
}

type TextosFormato = Translations['formatTemplates'];

type TextosResumen = Pick<TextosFormato, 'a4' | 'letter' | 'legal'>
  & Partial<Pick<TextosFormato, 'mixtoResumenHojaOriginal' | 'mixtoResumenLetraOriginal' | 'mixtoResumenMargenesOriginal' | 'mixtoResumenTodoOriginal'>>;

/**
 * Resumen corto de una plantilla para tarjetas/selects: "A4 · Arial 11 / 1,5 · 25/25/30/20 mm".
 * Con `tf`, la hoja sale con su nombre en el idioma de la interfaz (Carta,
 * Oficio…) en vez del código; los números siguen el formato del idioma.
 *
 * Formato mixto: lo que el documento toma del original se dice así («Hoja
 * del original»), no con la hoja, la letra o los márgenes de la plantilla,
 * que no se usan. Una mezcla ilegible es todo del original (lo que hace el
 * servidor: la Opción 2).
 */
export function describeTemplate(t: FormatTemplate, locale = 'es', tf?: TextosResumen): string {
  const n = (v: number) => (Number.isFinite(v) ? v.toLocaleString(locale, { maximumFractionDigits: 2 }) : String(v));
  const norm = t.esMixta ? normalizarAspectos(t.aspectos) : null;
  const a = norm ? (norm.ok ? norm.valor : ASPECTOS_ILEGIBLES) : null;
  const horizontal = t.pageOrientation === 'landscape' && (!a || a.orientacion === 'plantilla');
  const hoja = (tf?.[t.pageFormat] || t.pageFormat.toUpperCase()) + (horizontal ? ' ⟷' : '');
  const letra = `${t.fontFamily} ${n(t.fontSizePt)} / ${n(t.lineHeight)}`;
  const margenes = `${[t.marginTopMm, t.marginBottomMm, t.marginLeftMm, t.marginRightMm].map(n).join('/')} mm`;
  if (a && tf?.mixtoResumenHojaOriginal && tf.mixtoResumenLetraOriginal && tf.mixtoResumenMargenesOriginal && tf.mixtoResumenTodoOriginal) {
    const [hojaO, letraO, margenesO] = [a.hoja, a.tipografia, a.margenes].map((x) => x === 'original');
    if (hojaO && letraO && margenesO) return tf.mixtoResumenTodoOriginal;
    return [hojaO ? tf.mixtoResumenHojaOriginal : hoja, letraO ? tf.mixtoResumenLetraOriginal : letra, margenesO ? tf.mixtoResumenMargenesOriginal : margenes].join(' · ');
  }
  return `${hoja} · ${letra} · ${margenes}`;
}

/**
 * Mensaje de error de guardar/duplicar/fijar una plantilla, en el idioma de
 * la interfaz. processing-ms valida en español (`validar()` de
 * format-templates.service.ts): en español se muestra su texto, que es el
 * más preciso; en los demás idiomas se traduce lo reconocible y el resto cae
 * a un mensaje genérico — nunca se pinta el español a un usuario en alemán.
 */
export function mensajeErrorFormato(data: unknown, tf: TextosFormato, locale: string, generico: string = tf.errorGeneric, mezcla?: unknown): string {
  const base = mensajeErrorBase(data, tf, locale, generico);
  // Formato mixto: el campo vacío está en una sección que la mezcla toma del
  // original (atenuada, no se puede tocar). Un backend que aún lo valida lo
  // rechaza: se dice cómo corregirlo.
  const d = (data && typeof data === 'object' ? data : {}) as { message?: unknown };
  const msg = typeof d.message === 'string' ? d.message : '';
  const seccion = /encabezado está activo/i.test(msg) ? 'encabezado' : /fórmula de cierre está activa/i.test(msg) ? 'cierre' : null;
  const n = mezcla == null ? null : normalizarAspectos(mezcla);
  if (seccion && n?.ok && n.valor && n.valor[seccion] === 'original') return `${base} ${tf.mixtoErrSeccionDelOriginal}`;
  return base;
}

function mensajeErrorBase(data: unknown, tf: TextosFormato, locale: string, generico: string): string {
  const d = (data && typeof data === 'object' ? data : {}) as { message?: unknown; status?: unknown; lockedFields?: unknown };
  const msg = typeof d.message === 'string' ? d.message.trim() : '';
  const status = typeof d.status === 'number' ? d.status : null;
  if (locale === 'es' && msg && (status === 400 || status === 409)) return msg;
  if (Array.isArray(d.lockedFields) && d.lockedFields.includes('aspectos')) return tf.mixtoBloqueado;
  if (Array.isArray(d.lockedFields) && d.lockedFields.length) return tf.lockedNotice;
  if (/formato mixto no está disponible/i.test(msg)) return tf.mixtoNoDisponible;
  if (/necesita encabezado, pie o numeración/i.test(msg)) return tf.mixtoSinBandas;
  if (/aún no está disponible/i.test(msg)) return tf.mixtoCombinacion;
  if (/va con sus márgenes/i.test(msg)) return tf.mixtoErrCoherencia;
  if (/no hace falta una plantilla/i.test(msg)) return tf.mixtoTodoOriginal;
  if (/encabezado está activo/i.test(msg)) return tf.errHeaderEmpty;
  if (/fórmula de cierre está activa/i.test(msg)) return tf.errClosingEmpty;
  if (/nombre es obligatorio/i.test(msg)) return tf.errNameRequired;
  if (status === 400 && /debe(n)? (estar|ser)|no puede superar|pueden sumar como mucho|inválid|desconocid/i.test(msg)) return tf.errInvalidValue;
  return generico;
}

/** Nombre de la plantilla en el idioma de la interfaz (fallback al nombre guardado). */
export function nombreDePlantilla(tpl: { name: string; nameI18n?: Record<string, string> | null }, locale: string): string {
  return tpl.nameI18n?.[locale]?.trim() || tpl.name;
}
